import { datadogLogs } from '@datadog/browser-logs';
import {
  type AuthorizationError,
  AuthorizationNotifier,
  AuthorizationRequest,
  type AuthorizationResponse,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  RedirectRequestHandler,
  TokenRequest,
  type TokenResponse,
} from '@openid/appauth';
import type React from 'react';
import { createContext, useContext } from 'react';

// Returns true if navigation to the IdP end-session endpoint was initiated.
// Returns false (without throwing) when the issuer config has no
// endSessionEndpoint, so callers can fall back to a local logout. (OVT-5794)
export const clientHostedLogout = async (
  issuerUrl: string,
  redirectUrl?: string,
  logoutMessage?: string,
): Promise<boolean> => {
  const config = await AuthorizationServiceConfiguration.fetchFromIssuer(
    issuerUrl,
    new FetchRequestor(),
  );

  let logoutRedirectUrl = redirectUrl || window.location.origin;
  logoutRedirectUrl =
    logoutRedirectUrl.indexOf('http') === -1
      ? `http://${logoutRedirectUrl}`
      : logoutRedirectUrl;

  if (!config.endSessionEndpoint) {
    datadogLogs.logger.error(
      'AuthProvider.clientHostedLogout - endSessionEndpoint not found for Issuer',
      {
        clientHostedLoginURL: issuerUrl,
        clientHostedRedirectUrl: logoutRedirectUrl,
      },
    );
    return false;
  }

  const targetUrl = new URL(logoutRedirectUrl);
  // set logoutRedirectUrl here ^^^^^^ to 'http://localhost:3000/org/atbstaging | /atbe2e' for local testing
  targetUrl.searchParams.delete('useHostedLogin');

  // Pass logout message in URL parameters for immediate display
  if (logoutMessage && logoutMessage.trim()) {
    targetUrl.searchParams.set('logoutMessage', logoutMessage.trim());
  }

  logoutRedirectUrl = targetUrl.toString();

  window.location.href = `${config.endSessionEndpoint}?TargetResource=${logoutRedirectUrl}`;
  return true;
};

type AuthorizationRequestParams = {
  issuerUrl: string;
  redirectUrl: string;
  clientId: string;
};

export interface HandleCallbackParams extends AuthorizationRequestParams {
  errorHandler?: () => Promise<void>;
  successHandler?: (tokenResponse: TokenResponse) => Promise<void>;
}

type AuthContextType = {
  loginPing: (
    params: AuthorizationRequestParams & { loginScope: string },
  ) => Promise<void>;
  refreshToken: (
    params: AuthorizationRequestParams & { refreshTokenStr: string },
  ) => Promise<TokenResponse | undefined>;
  handleCallback: (params: HandleCallbackParams) => Promise<void>;
  loginWithSSOSession: (params: HandleCallbackParams) => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const authorizationHandler = new RedirectRequestHandler();
const notifier = new AuthorizationNotifier();
authorizationHandler.setAuthorizationNotifier(notifier);
const tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const loginPing = async ({
    issuerUrl,
    redirectUrl,
    clientId,
    loginScope,
  }: AuthorizationRequestParams & { loginScope: string }) => {
    const loggedOutMessage = window.localStorage.getItem('loggedOutMessage');
    const request = new AuthorizationRequest({
      client_id: clientId,
      redirect_uri: `${redirectUrl}?authCode=true`,
      scope: loginScope,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      // OVT-5794: When we have a loggedOutMessage to surface, also force the
      // IdP to re-prompt via `prompt=login`. The Auth0Provider pre-flight
      // already tries to kill the IdP SSO session via clientHostedLogout, but
      // that path requires public-settings to be loadable at boot — when it
      // isn't (e.g. customUrl doesn't resolve), prompt=login is what stops
      // PingFed from silently re-auth'ing via its still-alive SSO cookie.
      extras: {
        response_mode: 'fragment',
        ...(loggedOutMessage && { loggedOutMessage, prompt: 'login' }),
      },
    });

    AuthorizationServiceConfiguration.fetchFromIssuer(
      issuerUrl,
      new FetchRequestor(),
    )
      .then((config) => {
        authorizationHandler.performAuthorizationRequest(config, request);
        window.localStorage.removeItem('loggedOutMessage');
      })
      .catch((error) => {
        datadogLogs.logger.error(
          'loginPing: Configuration issue for fetch Issuer',
          {
            loginError: 'Failed to fetch config from issuerUrl',
            error,
          },
        );
      });
  };

  const refreshToken = async ({
    issuerUrl,
    redirectUrl,
    clientId,
    refreshTokenStr,
  }: AuthorizationRequestParams & { refreshTokenStr: string }) => {
    const tokenRequest = new TokenRequest({
      client_id: clientId,
      redirect_uri: redirectUrl,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      refresh_token: refreshTokenStr,
    });
    try {
      const config = await AuthorizationServiceConfiguration.fetchFromIssuer(
        issuerUrl,
        new FetchRequestor(),
      );
      const tokenResponse = await tokenHandler.performTokenRequest(
        config,
        tokenRequest,
      );
      return tokenResponse;
    } catch (error) {
      datadogLogs.logger.error('Refresh external token error: ', { error });
      throw error;
    }
  };

  const getAuthorizationListener = ({
    issuerUrl,
    redirectUrl,
    clientId,
    errorHandler,
    successHandler,
  }: HandleCallbackParams) => {
    return async (
      authorizationRequest: AuthorizationRequest,
      authorizationResponse: AuthorizationResponse | null,
      authorizationError: AuthorizationError | null,
    ) => {
      if (authorizationError) {
        console.error(authorizationError);
        errorHandler?.();
      } else {
        const tokenRequest = await new TokenRequest({
          client_id: clientId,
          redirect_uri: `${redirectUrl}?authCode=true`,
          grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
          code: authorizationResponse?.code || '',
          extras: {
            code_verifier: authorizationRequest?.internal?.code_verifier || '',
          },
        });

        try {
          const config =
            await AuthorizationServiceConfiguration.fetchFromIssuer(
              issuerUrl,
              new FetchRequestor(),
            );
          const tokenResponse =
            (await tokenHandler.performTokenRequest(config, tokenRequest)) ||
            {};

          await successHandler?.(tokenResponse);
        } catch (error) {
          datadogLogs.logger.error('Exchange client token Error', {
            error,
            loginError: 'Exchange client token Error',
          });
        }
      }
    };
  };

  const handleCallback = async (params: HandleCallbackParams) => {
    notifier.setAuthorizationListener(getAuthorizationListener(params));

    // Check for authorization response before attempting to process
    const hasAuthResponse =
      window.location.hash.includes('code=') ||
      window.location.search.includes('code=') ||
      window.location.hash.includes('error=');

    if (!hasAuthResponse) {
      await params.errorHandler?.();
      return;
    }

    await authorizationHandler.completeAuthorizationRequestIfPossible();
  };

  const loginWithSSOSession = async (params: HandleCallbackParams) => {
    let ssoReuseListenerTriggered = false;

    const deafultAuthListener = getAuthorizationListener(params);

    const wrappedListener = async (
      request: AuthorizationRequest,
      response: AuthorizationResponse | null,
      error: AuthorizationError | null,
    ) => {
      ssoReuseListenerTriggered = true;

      await deafultAuthListener(request, response, error);
    };

    notifier.setAuthorizationListener(wrappedListener);

    const timeoutPromise = new Promise<boolean>((resolve) =>
      setTimeout(() => {
        resolve(false);
      }, 5000),
    );

    const authPromise = authorizationHandler
      .completeAuthorizationRequestIfPossible()
      .then(() => ssoReuseListenerTriggered)
      .catch(() => false);

    try {
      const ssoSuccess = await Promise.race([authPromise, timeoutPromise]);
      return ssoSuccess;
    } catch {
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{ loginPing, refreshToken, handleCallback, loginWithSSOSession }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
